/**
 * Invoice Approval Detail Modal
 * For Department Managers to review and approve/reject invoices
 * Features:
 * - Split view: Invoice PDF/Image on left, Details on right
 * - Invoice details matching finance view
 * - GL Account and Budget Line assignment per invoice line
 * - Global GL selector with per-line override
 * - Approve/Reject with comments
 * - Audit log/history of invoice actions
 * - Approval disabled until all lines have GL and Budget assigned
 */

import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { X, CheckCircle, XCircle, FileText, Clock, User, MessageSquare, Download, AlertCircle, Sparkles, ThumbsUp, ThumbsDown } from 'lucide-react';
import OCRConfidenceBadge from './OCRConfidenceBadge';

const InvoiceApprovalDetail = ({ invoiceId, onClose, onActionComplete }) => {
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [lines, setLines] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [approvalId, setApprovalId] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // GL and Budget assignment state
  const [availableGLAccounts, setAvailableGLAccounts] = useState([]);
  const [budgetLinesByGL, setBudgetLinesByGL] = useState({}); // { glAccountId: [budgetLines] }
  const [globalGL, setGlobalGL] = useState('');
  const [globalBudget, setGlobalBudget] = useState('');
  const [lineAssignments, setLineAssignments] = useState({}); // { lineId: { glAccountId, budgetLineId } }
  const [validationErrors, setValidationErrors] = useState([]);
  const [savingAssignments, setSavingAssignments] = useState(false);

  // AI GL Suggestion state
  const [aiSuggestions, setAiSuggestions] = useState({}); // { lineId: { glAccountId, accountCode, accountName, confidence, reason, budgetSuggestion } }
  const [aiLoading, setAiLoading] = useState(false);
  const [aiApplied, setAiApplied] = useState({}); // { lineId: true } — tracks which lines accepted AI suggestion
  const [aiVerified, setAiVerified] = useState(false); // Must be checked before approval when AI was used
  const [docError, setDocError] = useState(false);

  useEffect(() => {
    if (invoiceId) {
      fetchInvoiceDetails();
      fetchAuditLog();
    }
  }, [invoiceId]);

  // Close modal on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // true when this is a Draft/New invoice with no workflow yet
  const isDraftMode = !approvalId;

  // Approve button is enabled only when every line has a GL account assigned
  const allLinesHaveGL = lines.length === 0 || lines.every(
    line => !!lineAssignments[line.InvoiceLineID]?.glAccountId
  );

  useEffect(() => {
    if (approvalId) {
      fetchAvailableGLAccounts();
      fetchAISuggestions();
    } else if (invoiceId) {
      // Draft invoice — load GL accounts directly by invoiceId
      fetchAvailableGLAccountsForInvoice();
    }
  }, [approvalId, invoiceId]);

  useEffect(() => {
    // Initialize line assignments from existing data
    if (lines.length > 0) {
      const assignments = {};
      lines.forEach(line => {
        assignments[line.InvoiceLineID] = {
          glAccountId: line.GLAccountID || '',
          budgetLineId: line.BudgetLineID || ''
        };
        // Fetch budgets for pre-assigned GLs
        if (line.GLAccountID) {
          fetchBudgetLinesForGL(line.GLAccountID);
        }
      });
      setLineAssignments(assignments);
    }
  }, [lines]);

  const fetchInvoiceDetails = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/invoices/${invoiceId}`);
      setInvoice(response.data.invoice);
      setLines(response.data.lines || []);
      
      // Extract ApprovalID only if THIS user is the assigned approver for the current step
      const approvals = response.data.approvals || [];
      const myApproval = approvals.find(a =>
        (a.IsCurrentApprover === true || a.IsCurrentApprover === 1) &&
        a.UserID === currentUser?.userId
      );
      if (myApproval) {
        setApprovalId(myApproval.ApprovalID);
      }
      // If user is not the current approver, approvalId stays null.
      // isDraftMode = true will be used, but button visibility uses invoice.Status
      // to differentiate Draft (GL-assign+submit) from Pending (read-only oversight).
    } catch (error) {
      console.error('Failed to load invoice:', error);
      alert('Failed to load invoice details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLog = async () => {
    try {
      const response = await apiClient.get(`/audit-logs/invoice/${invoiceId}`);
      
      setAuditLog(response.data || []);
    } catch (error) {
      console.error('Failed to load audit log:', error);
      // Audit log is optional, don't show error to user
      setAuditLog([]);
    }
  };

  const fetchAvailableGLAccounts = async () => {
    try {
      const response = await apiClient.get(`/approvals/${approvalId}/available-gl-accounts`);
      setAvailableGLAccounts(response.data || []);
    } catch (error) {
      console.error('Failed to load GL accounts:', error);
    }
  };

  const fetchAvailableGLAccountsForInvoice = async () => {
    try {
      const response = await apiClient.get(`/approvals/invoice/${invoiceId}/available-gl-accounts`);
      setAvailableGLAccounts(response.data || []);
    } catch (error) {
      console.error('Failed to load GL accounts for invoice:', error);
    }
  };

  const fetchAISuggestions = async () => {
    try {
      setAiLoading(true);
      const response = await apiClient.get(`/approvals/${approvalId}/suggest-gl`);

      const sugMap = {};
      (response.data.suggestions || []).forEach(s => {
        if (s.suggestion) {
          sugMap[s.invoiceLineId] = s.suggestion;
        }
      });
      setAiSuggestions(sugMap);
    } catch (error) {
      console.error('Failed to load AI suggestions:', error);
      // Non-critical — suggestion failure shouldn't block workflow
    } finally {
      setAiLoading(false);
    }
  };

  const applyAISuggestions = () => {
    const newAssignments = { ...lineAssignments };
    const newApplied = {};
    let appliedCount = 0;

    lines.forEach(line => {
      const suggestion = aiSuggestions[line.InvoiceLineID];
      if (suggestion) {
        const current = newAssignments[line.InvoiceLineID];
        // Only apply if line doesn't already have a GL assigned
        if (!current?.glAccountId) {
          newAssignments[line.InvoiceLineID] = {
            glAccountId: suggestion.glAccountId,
            budgetLineId: suggestion.budgetSuggestion?.budgetLineId || ''
          };
          newApplied[line.InvoiceLineID] = true;
          appliedCount++;
          fetchBudgetLinesForGL(suggestion.glAccountId);
        }
      }
    });

    if (appliedCount > 0) {
      setLineAssignments(newAssignments);
      setAiApplied(prev => ({ ...prev, ...newApplied }));
      setAiVerified(false); // Reset verification when new suggestions applied
    }
  };

  const fetchBudgetLinesForGL = async (glAccountId) => {
    if (!glAccountId) {
      return;
    }

    if (budgetLinesByGL[glAccountId]) {
      return; // Already loaded
    }
    try {
      const url = approvalId
        ? `/approvals/${approvalId}/budget-lines/${glAccountId}`
        : `/approvals/invoice/${invoiceId}/budget-lines/${glAccountId}`;
      const response = await apiClient.get(url);
      
      setBudgetLinesByGL(prev => ({
        ...prev,
        [glAccountId]: response.data || []
      }));
    } catch (error) {
      console.error('Failed to load budget lines:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const handleGlobalGLChange = (glAccountId) => {
    setGlobalGL(glAccountId);
    setGlobalBudget(''); // Reset global budget when GL changes
    
    if (glAccountId) {
      // Apply to all lines
      const newAssignments = {};
      lines.forEach(line => {
        newAssignments[line.InvoiceLineID] = {
          glAccountId: glAccountId,
          budgetLineId: '' // Reset budget when GL changes
        };
      });
      setLineAssignments(newAssignments);
      
      // Fetch budget lines for this GL
      fetchBudgetLinesForGL(glAccountId);
    }
  };

  const handleGlobalBudgetChange = (budgetLineId) => {
    setGlobalBudget(budgetLineId);
    
    if (budgetLineId && globalGL) {
      // Apply to all lines that have the global GL selected
      const newAssignments = { ...lineAssignments };
      lines.forEach(line => {
        if (newAssignments[line.InvoiceLineID]?.glAccountId === globalGL) {
          newAssignments[line.InvoiceLineID] = {
            ...newAssignments[line.InvoiceLineID],
            budgetLineId: budgetLineId
          };
        }
      });
      setLineAssignments(newAssignments);
    }
  };

  const handleLineGLChange = (lineId, glAccountId) => {
    setLineAssignments(prev => ({
      ...prev,
      [lineId]: {
        glAccountId: glAccountId,
        budgetLineId: '' // Reset budget when GL changes
      }
    }));
    
    // Fetch budget lines for this GL
    if (glAccountId) {
      fetchBudgetLinesForGL(glAccountId);
    }
  };

  const handleLineBudgetChange = (lineId, budgetLineId) => {
    setLineAssignments(prev => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        budgetLineId: budgetLineId
      }
    }));
  };
  // Calculate adjusted remaining amount for a budget line
  // Subtracts amounts from lines that have already selected this budget
  const getAdjustedRemainingAmount = (budgetLineId, currentLineId) => {
    const budgetLine = Object.values(budgetLinesByGL)
      .flat()
      .find(b => b.BudgetLineID === budgetLineId);
    
    if (!budgetLine) return 0;

    let adjustedRemaining = budgetLine.RemainingAmount;

    // Subtract amounts from other lines using this same budget line
    lines.forEach(line => {
      if (line.InvoiceLineID === currentLineId) return; // Skip current line
      
      const assignment = lineAssignments[line.InvoiceLineID];
      if (assignment?.budgetLineId === budgetLineId) {
        const lineAmount = line.LineAmount || line.Amount || (line.Quantity * line.UnitPrice) || 0;
        adjustedRemaining -= lineAmount;
      }
    });

    return Math.max(0, adjustedRemaining); // Never show negative
  };
  const validateAssignments = () => {
    const errors = [];
    const warnings = [];
    lines.forEach(line => {
      const assignment = lineAssignments[line.InvoiceLineID];
      if (!assignment || !assignment.glAccountId) {
        errors.push(`Line ${line.LineNumber}: GL Account not selected`);
      }
      if (!assignment || !assignment.budgetLineId) {
        warnings.push(`Line ${line.LineNumber}: No budget line — will go to overflow`);
      }
    });
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const saveAssignments = async () => {
    if (!validateAssignments()) {
      return false;
    }

    setSavingAssignments(true);
    try {
      const assignmentsArray = Object.keys(lineAssignments).map(lineId => ({
        invoiceLineId: lineId,
        glAccountId: lineAssignments[lineId].glAccountId,
        budgetLineId: lineAssignments[lineId].budgetLineId || null
      }));

      if (isDraftMode) {
        // Draft invoice — save via invoice-level endpoint
        await apiClient.post(
          `/approvals/invoice/${invoiceId}/assign-gl-budget`,
          { lineAssignments: assignmentsArray }
        );
      } else {
        // Build AI suggestions map for learning feedback
        const aiSuggestionsPayload = {};
        Object.keys(aiSuggestions).forEach(lineId => {
          aiSuggestionsPayload[lineId] = {
            glAccountId: aiSuggestions[lineId].glAccountId,
            confidence: aiSuggestions[lineId].confidence
          };
        });

        await apiClient.post(
          `/approvals/${approvalId}/assign-gl-budget`,
          { lineAssignments: assignmentsArray, aiSuggestions: aiSuggestionsPayload }
        );
      }

      return true;
    } catch (error) {
      console.error('Failed to save assignments:', error);
      alert('Failed to save GL and budget assignments');
      return false;
    } finally {
      setSavingAssignments(false);
    }
  };

  const handleSaveAndSubmit = async () => {
    const saved = await saveAssignments();
    if (!saved) return;

    try {
      setSubmitting(true);
      const response = await apiClient.post(`/invoices/${invoiceId}/submit`);
      const isAutoApproved = response.data.status === 'Approved';
      if (isAutoApproved) {
        alert('Invoice auto-approved — no approvers are configured for this department.');
      } else {
        const levels = response.data.approvals?.length || 1;
        alert(`Invoice submitted for approval (${levels} approval${levels !== 1 ? 's' : ''} required).`);
      }
      if (onActionComplete) onActionComplete();
      onClose();
    } catch (error) {
      console.error('Submit error:', error);
      alert(error.response?.data?.message || 'Failed to submit invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    // Check AI verification if AI suggestions were used
    const hasAiApplied = Object.keys(aiApplied).length > 0;
    if (hasAiApplied && !aiVerified) {
      alert('Please verify the AI-suggested GL and Budget assignments before approving. Tick the verification checkbox to confirm you have reviewed them.');
      return;
    }

    // First validate and save assignments
    if (!validateAssignments()) {
      alert('Please assign GL Account and Budget Line to all invoice lines before approving');
      return;
    }

    const saved = await saveAssignments();
    if (!saved) {
      return;
    }

    if (!comments.trim() && !confirm('Approve without comments?')) {
      return;
    }

    if (!approvalId) {
      alert('Approval ID not found');
      return;
    }

    try {
      setSubmitting(true);
      
      await apiClient.post(
        `/approvals/${approvalId}/approve`,
        { comments: comments.trim() }
      );

      alert('Invoice approved successfully!');
      setShowApproveModal(false);
      if (onActionComplete) onActionComplete();
      onClose();
    } catch (error) {
      console.error('Approve error:', error);
      alert(error.response?.data?.message || 'Failed to approve invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!comments.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    if (!approvalId) {
      alert('Approval ID not found');
      return;
    }

    try {
      setSubmitting(true);
      
      await apiClient.post(
        `/approvals/${approvalId}/reject`,
        { comments: comments.trim() }
      );

      alert('Invoice rejected');
      setShowRejectModal(false);
      if (onActionComplete) onActionComplete();
      onClose();
    } catch (error) {
      console.error('Reject error:', error);
      alert(error.response?.data?.message || 'Failed to reject invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    if (!amount) return '£0.00';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const ACTION_LABELS = {
    CURRENCY_RECALCULATE:  'Currency updated',
    GL_ASSIGNMENT_UPDATED: 'GL & budget assigned',
    GL_ASSIGNED:           'GL & budget assigned',
    INVOICE_SUBMITTED:     'Submitted for approval',
    INVOICE_APPROVED:      'Approved',
    INVOICE_REJECTED:      'Rejected',
    INVOICE_CREATED:       'Invoice created',
    INVOICE_UPDATED:       'Invoice updated',
    APPROVAL_SENT:         'Sent for approval',
    APPROVAL_REQUESTED:    'Sent for approval',
    TAKEOVER:              'Approval taken over',
    FINANCE_TAKEOVER:      'Finance admin took over',
    OCR_PROCESSED:         'OCR scan completed',
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return null;
  }

  const documentUrl = invoice.DocumentUrl || invoice.DocumentURL || invoice.BlobURL || invoice.DocumentPath || invoice.BlobUrl;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-7xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Invoice Approval</h2>
            <p className="text-sm text-gray-600 mt-1">
              Invoice #{invoice.InvoiceNumber} - {invoice.VendorName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Main Content - Split View */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Document Viewer (50%) */}
          <div className="w-1/2 border-r bg-gray-50 flex flex-col">
            <div className="p-4 bg-white border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Original Document
              </h3>
              {documentUrl && (
                <a
                  href={documentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm"
                >
                  <Download className="w-4 h-4" />
                  Download
                </a>
              )}
            </div>
            <div className="flex-1 overflow-hidden p-4">
              {documentUrl && !docError ? (
                <iframe
                  src={documentUrl}
                  className="w-full h-full rounded-lg shadow-sm border border-gray-200"
                  title="Invoice Document"
                  onError={() => setDocError(true)}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400">
                  <FileText className="w-14 h-14 opacity-20" />
                  <p className="text-sm font-medium">
                    {docError ? 'Document could not be loaded' : 'No document uploaded'}
                  </p>
                  {documentUrl && docError && (
                    <a
                      href={documentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 text-sm hover:underline flex items-center gap-1"
                    >
                      <Download className="w-4 h-4" />
                      Try opening directly
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Details & Actions (50%) */}
          <div className="w-1/2 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6">
              {/* Invoice Details */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Details</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase">Invoice Number</label>
                      <p className="mt-1 text-sm font-semibold text-gray-900">{invoice.InvoiceNumber}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase">Invoice Date</label>
                      <p className="mt-1 text-sm text-gray-900">{formatDate(invoice.InvoiceDate)}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase">Vendor</label>
                      <p className="mt-1 text-sm text-gray-900">{invoice.VendorName}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase">Department</label>
                      <p className="mt-1 text-sm text-gray-900">{invoice.DepartmentName}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase">Sub Total</label>
                      <p className="mt-1 text-sm text-gray-900">{formatCurrency(invoice.SubTotal, invoice.Currency)}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase">Tax Amount</label>
                      <p className="mt-1 text-sm text-gray-900">{formatCurrency(invoice.TaxAmount, invoice.Currency)}</p>
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs font-medium text-gray-500 uppercase">Total Amount</label>
                      <p className="mt-1 text-2xl font-bold text-gray-900">{formatCurrency(invoice.TotalAmount, invoice.Currency)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* GL & Budget Assignment Section */}
              {lines && lines.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Lines - Assign GL & Budget</h3>
                  
                  {/* Global GL & Budget Selectors */}
                  <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Quick Assign GL to All Lines
                        </label>
                        <select
                          value={globalGL}
                          onChange={(e) => handleGlobalGLChange(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">-- Select GL Account for all lines --</option>
                          {availableGLAccounts.map(gl => (
                            <option key={gl.GLAccountID} value={gl.GLAccountID}>
                              {gl.DisplayName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Quick Assign Budget to All Lines
                        </label>
                        <select
                          value={globalBudget}
                          onChange={(e) => handleGlobalBudgetChange(e.target.value)}
                          disabled={!globalGL}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="">-- Select Budget for all lines --</option>
                          {globalGL && budgetLinesByGL[globalGL]?.map(budget => {
                            const adjustedRemaining = getAdjustedRemainingAmount(budget.BudgetLineID, null);
                            const description = budget.LineDescription || budget.BudgetName;
                            return (
                              <option key={budget.BudgetLineID} value={budget.BudgetLineID}>
                                {description} ({budget.AccountCode} - {budget.AccountName}) - Avail: {formatCurrency(adjustedRemaining, invoice.Currency)}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-gray-600">
                      Select GL account first, then budget. This applies to all lines. You can still override individual lines below.
                    </p>
                  </div>

                  {/* AI Suggestions Banner */}
                  {Object.keys(aiSuggestions).length > 0 && (
                    <div className="mb-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-2">
                          <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="font-semibold text-purple-900">AI GL & Budget Suggestions Available</h4>
                            <p className="text-sm text-purple-700 mt-1">
                              {Object.keys(aiSuggestions).length} of {lines.length} lines have AI-suggested GL codes and budget lines based on vendor history and past assignments.
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={applyAISuggestions}
                          className="ml-4 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 flex-shrink-0"
                        >
                          <Sparkles className="w-4 h-4" />
                          Apply All
                        </button>
                      </div>
                    </div>
                  )}

                  {/* AI Verification Warning — shown after AI suggestions are applied */}
                  {Object.keys(aiApplied).length > 0 && (
                    <div className="mb-4 bg-amber-50 border-2 border-amber-400 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="font-bold text-amber-900 text-base">
                            ⚠️ AI Auto-Filled — Verification Required
                          </h4>
                          <p className="text-sm text-amber-800 mt-1">
                            GL accounts and budget lines were <strong>automatically selected by AI</strong> based on vendor
                            history and past assignments. <strong>You must review and verify</strong> these selections are
                            correct before approving this invoice.
                          </p>
                          <label className="mt-3 flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={aiVerified}
                              onChange={(e) => setAiVerified(e.target.checked)}
                              className="w-5 h-5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                            />
                            <span className="text-sm font-semibold text-amber-900">
                              I have reviewed and verified the AI-suggested GL and Budget assignments
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {aiLoading && (
                    <div className="mb-4 bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center gap-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                      <span className="text-sm text-gray-600">Analysing vendor history for GL & budget suggestions...</span>
                    </div>
                  )}

                  {/* Validation Errors */}
                  {validationErrors.length > 0 && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-red-900 mb-2">Assignment Required</h4>
                          <ul className="list-disc list-inside space-y-1">
                            {validationErrors.map((error, index) => (
                              <li key={index} className="text-sm text-red-700">{error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Lines Table with GL & Budget Dropdowns */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">GL Account *</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Budget Line *</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase w-20" title="AI-suggested GL code based on vendor history">AI Hint</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {lines.map((line) => {
                          const assignment = lineAssignments[line.InvoiceLineID] || {};
                          const selectedGL = assignment.glAccountId;
                          const budgetOptions = selectedGL ? (budgetLinesByGL[selectedGL] || []) : [];
                          const suggestion = aiSuggestions[line.InvoiceLineID];
                          const isAiApplied = aiApplied[line.InvoiceLineID];
                          const aiMatchesSelected = suggestion && selectedGL === suggestion.glAccountId;

                          return (
                            <tr key={line.InvoiceLineID} className={isAiApplied && aiMatchesSelected ? 'bg-purple-50' : ''}>
                              <td className="px-4 py-2 text-sm text-gray-900">{line.Description || line.ItemDescription || 'N/A'}</td>
                              <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">
                                {formatCurrency(line.LineAmount || line.Amount || (line.Quantity * line.UnitPrice), invoice.Currency)}
                              </td>
                              <td className="px-4 py-2">
                                <select
                                  value={selectedGL || ''}
                                  onChange={(e) => {
                                    handleLineGLChange(line.InvoiceLineID, e.target.value);
                                    // If user changes away from AI suggestion, mark as not AI-applied
                                    if (isAiApplied && e.target.value !== suggestion?.glAccountId) {
                                      setAiApplied(prev => { const n = {...prev}; delete n[line.InvoiceLineID]; return n; });
                                    }
                                  }}
                                  className={`w-full px-2 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500 ${
                                    !selectedGL ? 'border-red-300 bg-red-50' : isAiApplied && aiMatchesSelected ? 'border-purple-400 bg-purple-50' : 'border-gray-300'
                                  }`}
                                >
                                  <option value="">-- Select GL --</option>
                                  {availableGLAccounts.map(gl => (
                                    <option key={gl.GLAccountID} value={gl.GLAccountID}>
                                      {gl.DisplayName}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-4 py-2">
                                <select
                                  value={assignment.budgetLineId || ''}
                                  onChange={(e) => handleLineBudgetChange(line.InvoiceLineID, e.target.value)}
                                  disabled={!selectedGL}
                                  className={`w-full px-2 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                                    !assignment.budgetLineId && selectedGL ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                  }`}
                                >
                                  <option value="">-- Select Budget --</option>
                                  {budgetOptions.map(budget => {
                                    const adjustedRemaining = getAdjustedRemainingAmount(budget.BudgetLineID, line.InvoiceLineID);
                                    const lineAmount = line.LineAmount || line.Amount || (line.Quantity * line.UnitPrice) || 0;
                                    const willExceed = lineAmount > adjustedRemaining;
                                    const description = budget.LineDescription || budget.BudgetName;
                                    const fyLabel = budget.YearLabel ? `[${budget.YearLabel}] ` : '';
                                    
                                    return (
                                      <option 
                                        key={budget.BudgetLineID} 
                                        value={budget.BudgetLineID}
                                        style={willExceed ? {color: 'red'} : {}}
                                      >
                                        {fyLabel}{description} ({budget.AccountCode}) - Avail: {formatCurrency(adjustedRemaining, invoice.Currency)}
                                      </option>
                                    );
                                  })}
                                </select>
                              </td>
                              <td className="px-4 py-2 text-center">
                                {suggestion ? (
                                  <div className="flex flex-col items-center gap-1">
                                    <OCRConfidenceBadge confidence={suggestion.confidence} label={`${suggestion.reason}\n${suggestion.accountCode} - ${suggestion.accountName}`} />
                                    {!selectedGL && (
                                      <button
                                        onClick={() => {
                                          handleLineGLChange(line.InvoiceLineID, suggestion.glAccountId);
                                          setLineAssignments(prev => ({
                                            ...prev,
                                            [line.InvoiceLineID]: {
                                              glAccountId: suggestion.glAccountId,
                                              budgetLineId: suggestion.budgetSuggestion?.budgetLineId || ''
                                            }
                                          }));
                                          setAiApplied(prev => ({ ...prev, [line.InvoiceLineID]: true }));
                                          setAiVerified(false);
                                          fetchBudgetLinesForGL(suggestion.glAccountId);
                                        }}
                                        className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                                        title={`Apply: ${suggestion.accountCode} - ${suggestion.accountName}${suggestion.budgetSuggestion ? '\nBudget: ' + suggestion.budgetSuggestion.description : ''}`}
                                      >
                                        Apply
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Save Assignments Button */}
                  <div className="mt-4">
                    <button
                      onClick={saveAssignments}
                      disabled={savingAssignments || validationErrors.length > 0}
                      className="btn btn-primary w-full"
                    >
                      {savingAssignments ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Save GL & Budget Assignments
                        </>
                      )}
                    </button>
                    <p className="mt-2 text-xs text-gray-600 text-center">
                      Save your GL and Budget assignments before approving
                    </p>
                  </div>
                </div>
              )}

              {/* Audit Log / History */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Activity History
                </h3>
                <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                  {auditLog && auditLog.length > 0 ? (
                    auditLog.map((log, index) => (
                      <div key={index} className="p-3 hover:bg-gray-50">
                        <div className="flex items-start gap-2">
                          <User className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {log.ActorUsername || log.ActorEmail || 'System'}
                              </span>
                              <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                {ACTION_LABELS[log.Action] || (log.Action || '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}
                              </span>
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">{formatDateTime(log.Timestamp)}</div>
                            {log.ChangesSummary && (
                              <p className="mt-1 text-sm text-gray-600">{log.ChangesSummary}</p>
                            )}
                            {log.Comments && (
                              <div className="mt-1 bg-gray-50 border-l-2 border-blue-400 pl-3 py-2">
                                <div className="flex items-start gap-2">
                                  <MessageSquare className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
                                  <p className="text-sm text-gray-700 italic">{log.Comments}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 text-sm text-gray-500 text-center">
                      No activity history available
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="border-t bg-gray-50 p-6">
              {isDraftMode && (invoice?.Status === 'New' || invoice?.Status === 'Draft') ? (
                <div className="space-y-2">
                  <div className="text-xs text-gray-500 text-center mb-2">
                    This invoice is a Draft. Assign GL accounts above, then submit for approval.
                  </div>
                  <button
                    onClick={handleSaveAndSubmit}
                    disabled={submitting || savingAssignments}
                    className="btn btn-primary btn-lg w-full"
                  >
                    <CheckCircle className="w-5 h-5" />
                    {submitting || savingAssignments ? 'Saving & Submitting...' : 'Save GL & Submit for Approval'}
                  </button>
                </div>
              ) : isDraftMode ? (
                <div className="flex items-center justify-center gap-2 py-2 text-sm text-gray-500">
                  <Clock className="w-4 h-4" />
                  This invoice is awaiting approval by the assigned approver.
                </div>
              ) : (
                <>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowApproveModal(true)}
                      disabled={!allLinesHaveGL}
                      title={!allLinesHaveGL ? 'Assign a GL Account to all lines first' : undefined}
                      className="btn btn-success btn-lg flex-1 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <CheckCircle className="w-5 h-5" />
                      Approve
                    </button>
                    <button
                      onClick={() => setShowRejectModal(true)}
                      className="btn btn-danger btn-lg flex-1"
                    >
                      <XCircle className="w-5 h-5" />
                      Reject
                    </button>
                  </div>
                  {!allLinesHaveGL && lines.length > 0 && (
                    <p className="text-xs text-amber-600 text-center mt-2 flex items-center justify-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" />
                      Assign a GL Account to all lines to enable approval
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Approve Invoice
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                You are about to approve invoice <strong>#{invoice.InvoiceNumber}</strong> for {formatCurrency(invoice.TotalAmount, invoice.Currency)}.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Comments (Optional)
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  rows="4"
                  placeholder="Add any comments about this approval..."
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowApproveModal(false);
                    setComments('');
                  }}
                  className="btn btn-secondary flex-1"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleApprove}
                  className="btn btn-success flex-1"
                  disabled={submitting}
                >
                  {submitting ? 'Approving...' : 'Confirm Approval'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600" />
                Reject Invoice
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                You are about to reject invoice <strong>#{invoice.InvoiceNumber}</strong>. Please provide a reason.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Rejection Reason <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  rows="4"
                  placeholder="Explain why this invoice is being rejected..."
                  required
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setComments('');
                  }}
                  className="btn btn-secondary flex-1"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  className="btn btn-danger flex-1"
                  disabled={submitting || !comments.trim()}
                >
                  {submitting ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceApprovalDetail;
